import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Controller,
  ResolverOptions,
  ResolverResult,
  useForm,
} from 'react-hook-form';
import { useFetcher, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { parse, isValid, isAfter, startOfDay } from 'date-fns';
import { z } from 'zod';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { MIN_DISCHARGE_DATE, ResponsiveFormRow } from '@ldt/features/orders';
import {
  MetastaticBillingInformationFormSchema,
  BillingInformationFormSection,
} from '@ldt/features/orders/components/billing_information_form_section';
import { FileUpload } from '@ldt/features/orders/components/file_upload';
import { FormSectionCard } from '@ldt/features/orders/components/form_section_card';
import { LdtFormActions } from '@ldt/features/orders/components/ldt_form_actions';
import { LdtOrderFormHeader } from '@ldt/features/orders/components/order_form_header';
import { OrderingProviderFormSection } from '@ldt/features/orders/components/ordering_provider_form_section';
import {
  PatientInformationFormSchema,
  PatientInformationFormSection,
} from '@ldt/features/orders/components/patient_information_form_section';
import { PreventLeaveDuringUploadDialog } from '@ldt/features/orders/components/prevent_leave_during_upload_dialog';
import {
  computeLabProcedureDateWarning,
  getProcedureDateBeforeDateOfBirthError,
  SpecimenInformationFormSchema,
  SpecimenInformationFormSection,
} from '@ldt/features/orders/components/specimen_information_form_section';
import { useLdtFileUploads } from '@ldt/features/orders/hooks/use_ldt_file_uploads';
import { useOrderingProviderFormSection } from '@ldt/features/orders/hooks/use_ordering_provider_form_section';
import { OrderingProviderForm } from '@ldt/features/orders/ordering_provider_form';
import { canUseClinicalNotProvided } from '@ldt/features/orders/utils/clinical_not_provided';
import { allowSoftClinicalSubmit } from '@ldt/features/orders/utils/order_missing_info';
import { OrderRepository } from 'src/api/order_repository';
import { DecimalInput } from 'src/components/decimal_input';
import { SectionHeader } from 'src/components/section_header';
import { SelectList } from 'src/components/select_list';
import {
  processFetcherResponse,
  getProstateBiopsyTStageOptions,
  getProstateBiopsyTStages,
  safeEmptyToUndefined,
  submitOrderAction,
} from 'src/distribution_platforms/features/order_form';
import {
  useEffectiveOrganization,
  useIsLdtSalesOrCustomerSuccessUser,
  useIsOrderingPhysician,
} from 'src/hooks/use_auth';
import { useDebounce } from 'src/hooks/use_debounce';
import { useNewOrderSignal } from 'src/hooks/use_new_order_signal';
import {
  Ethnicity,
  LdtOrderDetailFieldsFragment,
  OrderStatus,
  OrderingPathway,
  PatientStatusAtBiopsy,
  PrimaryDiagnosisCode,
  SpecimenType,
  TStage,
  MetastaticDiagnosisCode,
  LdtUploadedFileFieldsFragment,
  UploadedFileCategory,
  UploadedFileStatus,
} from 'src/types';
import { OrderAction } from 'src/types/loaders';
import { WorkflowType } from 'src/types/workflows';
import { notifyError, notifySuccess } from 'src/utils';

const FORM_CONSTANTS = {
  PSA_MIN: 0,
} as const;

export type LdtMetastaticProstateClinicFormData = z.input<
  ReturnType<typeof createLdtMetastaticProstateClinicFormSchema>
>;

export const createLdtMetastaticProstateClinicFormSchema = (
  isCSOrSalesUser: boolean,
  orderingPathway: OrderingPathway,
  isEditingQCPendingOrder: boolean,
  allowTStageNotProvided = false,
  workflowType: WorkflowType = WorkflowType.CLINIC,
) => {
  const softClinicalSubmit = allowSoftClinicalSubmit(
    isCSOrSalesUser,
    isEditingQCPendingOrder,
    workflowType,
  );
  const acceptTStageNotProvided =
    allowTStageNotProvided && !isEditingQCPendingOrder;

  return z.object({
    orderingProviderId: softClinicalSubmit
      ? z.string()
      : z.string().min(1, 'Ordering Provider is required'),

    ...PatientInformationFormSchema.shape,

    ...MetastaticBillingInformationFormSchema(WorkflowType.CLINIC).shape,

    // PSA - required unless CS/Sales soft submit
    baselinePsa: z
      .number({
        error: (issue) => {
          if (issue.code === 'invalid_type') {
            return { message: 'Most Recent Pre-Treatment PSA is required' };
          }
          return null;
        },
      })
      .min(
        FORM_CONSTANTS.PSA_MIN,
        `PSA must be at least ${FORM_CONSTANTS.PSA_MIN} ng/mL`,
      )
      .nullable()
      .superRefine((val, ctx) => {
        if (!softClinicalSubmit && val === null) {
          ctx.addIssue({
            code: 'custom',
            message: 'Most Recent Pre-Treatment PSA is required',
          });
        }
      }),

    // T-stage - Not Provided may be included for parse/display; reject on QC edit
    tStage: z
      .enum(getProstateBiopsyTStages(isCSOrSalesUser, allowTStageNotProvided), {
        error: 'Clinical T-stage is required',
      })
      .superRefine((val, ctx) => {
        if (!acceptTStageNotProvided && val === TStage.NotProvided) {
          ctx.addIssue({
            code: 'custom',
            message: 'Clinical T-stage is required',
          });
        }
      }),

    // Specimen Information (required for CS/Sales QC Pending edit)
    ...SpecimenInformationFormSchema(
      SpecimenType.MetastaticProstateBiopsy,
      isCSOrSalesUser && isEditingQCPendingOrder,
    ).shape,

    // TRF: required for CS/Sales unless StickerTrfNonReflexive pathway
    testRequisitionForm:
      isCSOrSalesUser &&
      orderingPathway !== OrderingPathway.StickerTrfNonReflexive
        ? z
            .instanceof(File, { message: 'Test Requisition Form is required' })
            .nullable()
            .refine((file) => file !== null, {
              message: 'Test Requisition Form is required',
            })
        : z.instanceof(File).nullable().optional(),

    // Pathology Report: required when CS/Sales editing QC Pending
    pathologyReport: isEditingQCPendingOrder
      ? z
          .instanceof(File, { message: 'Pathology Report is required' })
          .nullable()
          .refine((file) => file !== null, {
            message: 'Pathology Report is required',
          })
      : z.instanceof(File).nullable().optional(),

    // Face Sheet: required when CS/Sales editing QC Pending
    faceSheet: isEditingQCPendingOrder
      ? z
          .instanceof(File, { message: 'Demographic / Face Sheet is required' })
          .nullable()
          .refine((file) => file !== null, {
            message: 'Demographic / Face Sheet is required',
          })
      : z.instanceof(File).nullable().optional(),
  });
};

export type LdtMetastaticProstateClinicWarnings = Partial<
  Record<keyof LdtMetastaticProstateClinicFormData, string>
>;

export const createMetastaticProstateClinicCustomResolver =
  (
    isCSOrSalesUser: boolean,
    orderingPathway: OrderingPathway,
    isEditingQCPendingOrder: boolean,
    warningsRef?: { current: LdtMetastaticProstateClinicWarnings },
    allowTStageNotProvided = false,
    workflowType: WorkflowType = WorkflowType.CLINIC,
  ) =>
  async (
    data: LdtMetastaticProstateClinicFormData,
    context: unknown,
    options: ResolverOptions<LdtMetastaticProstateClinicFormData>,
  ): Promise<
    ResolverResult<LdtMetastaticProstateClinicFormData> & {
      warnings: LdtMetastaticProstateClinicWarnings;
    }
  > => {
    const schema = createLdtMetastaticProstateClinicFormSchema(
      isCSOrSalesUser,
      orderingPathway,
      isEditingQCPendingOrder,
      allowTStageNotProvided,
      workflowType,
    );
    const zodResult = await zodResolver(schema)(data, context, options);

    const customErrors = { ...zodResult.errors };
    const warnings: LdtMetastaticProstateClinicWarnings = {};

    // PSA warning
    const psaWarning = computePsaWarning(data.baselinePsa ?? undefined);
    if (psaWarning) {
      warnings.baselinePsa = psaWarning;
    }

    if (isCSOrSalesUser && data.dateOfProcedure) {
      const dateWarning = computeLabProcedureDateWarning(
        data.dateOfProcedure,
        SpecimenType.MetastaticProstateBiopsy,
      );
      if (dateWarning) {
        warnings.dateOfProcedure = dateWarning;
      }
    }

    const procedureDateBeforeDobError = getProcedureDateBeforeDateOfBirthError(
      data,
      SpecimenType.MetastaticProstateBiopsy,
    );
    if (procedureDateBeforeDobError) {
      customErrors.dateOfProcedure = {
        type: 'custom',
        message: procedureDateBeforeDobError,
      };
    }

    // Discharge date: required for Hospital In/Outpatient
    if (
      data.patientStatus === PatientStatusAtBiopsy.HospitalInpatient ||
      data.patientStatus === PatientStatusAtBiopsy.HospitalOutpatient
    ) {
      if (!data.dateOfDischarge?.trim()) {
        customErrors.dateOfDischarge = {
          type: 'custom',
          message:
            'Date of Discharge is required for hospital inpatients/outpatients',
        };
      } else {
        try {
          const parsed = parse(data.dateOfDischarge, 'yyyy-MM-dd', new Date());
          if (!isValid(parsed)) {
            customErrors.dateOfDischarge = {
              type: 'custom',
              message: 'Date of Discharge must be a valid date',
            };
          } else if (parsed.getFullYear() < MIN_DISCHARGE_DATE.getFullYear()) {
            customErrors.dateOfDischarge = {
              type: 'custom',
              message: `Date of Discharge must be a valid date (year ${MIN_DISCHARGE_DATE.getFullYear()} or later)`,
            };
          } else {
            const today = startOfDay(new Date());
            if (isAfter(parsed, today)) {
              customErrors.dateOfDischarge = {
                type: 'custom',
                message: 'Date of Discharge cannot be in the future',
              };
            }
          }
        } catch {
          customErrors.dateOfDischarge = {
            type: 'custom',
            message: 'Date of Discharge must be a valid date',
          };
        }
      }
    }

    // Custom ICD-10 code required when "Other" is selected
    if (data.isOtherDiagnosisCodeSelected) {
      if (!data.customPrimaryDiagnosisCode?.trim()) {
        customErrors.customPrimaryDiagnosisCode = {
          type: 'custom',
          message: 'Custom ICD-10 Code is required when "Other" is selected',
        };
      }
    }

    // At least one ICD-10 diagnosis code must be selected (C61 or Other)
    if (
      data.primaryDiagnosisCodes.length === 0 &&
      !data.isOtherDiagnosisCodeSelected
    ) {
      customErrors.primaryDiagnosisCodes = {
        type: 'custom',
        message: 'At least one ICD-10 Primary Diagnosis Code is required',
      };
    }

    // Custom metastatic ICD-10 code required when "Other" is selected
    if (data.isOtherMetastaticDiagnosisCodeSelected) {
      if (!data.customMetastaticDiagnosisCode?.trim()) {
        customErrors.customMetastaticDiagnosisCode = {
          type: 'custom',
          message:
            'Custom ICD-10 Metastatic Code is required when "Other" is selected',
        };
      }
    }

    // At least one ICD-10 metastatic diagnosis code must be selected (C79.51 or Other)
    if (
      data.metastaticDiagnosisCodes.length === 0 &&
      !data.isOtherMetastaticDiagnosisCodeSelected
    ) {
      customErrors.metastaticDiagnosisCodes = {
        type: 'custom',
        message: 'At least one ICD-10 Metastatic Diagnosis Code is required',
      };
    }

    const result = {
      values: zodResult.values,
      errors: customErrors,
      warnings,
    } as ResolverResult<LdtMetastaticProstateClinicFormData> & {
      warnings: LdtMetastaticProstateClinicWarnings;
    };

    if (warningsRef) {
      warningsRef.current = result.warnings;
    }

    return result;
  };

export const createMetastaticProstateClinicDefaultFormValues = (
  initialOrder: LdtOrderDetailFieldsFragment,
): LdtMetastaticProstateClinicFormData => {
  let testRequisitionForm: File | null = null;
  let pathologyReport: File | null = null;
  let faceSheet: File | null = null;

  if (initialOrder.uploadedFiles?.edges) {
    const trfFile = initialOrder.uploadedFiles.edges.find(
      (edge) =>
        edge?.node?.category === UploadedFileCategory.TestRequisitionForm &&
        edge.node.status === UploadedFileStatus.Complete,
    );
    if (trfFile?.node) {
      testRequisitionForm = new File([], trfFile.node.filename);
    }

    const pathologyReportFile = initialOrder.uploadedFiles.edges.find(
      (edge) =>
        edge?.node?.category === UploadedFileCategory.PathologyReport &&
        edge.node.status === UploadedFileStatus.Complete,
    );
    if (pathologyReportFile?.node) {
      pathologyReport = new File([], pathologyReportFile.node.filename);
    }

    const faceSheetFile = initialOrder.uploadedFiles.edges.find(
      (edge) =>
        edge?.node?.category === UploadedFileCategory.FaceSheet &&
        edge.node.status === UploadedFileStatus.Complete,
    );
    if (faceSheetFile?.node) {
      faceSheet = new File([], faceSheetFile.node.filename);
    }
  }

  const primaryDiagnosisCodes = initialOrder.primaryDiagnosisCodes?.filter(
    (code): code is PrimaryDiagnosisCode => !!code,
  ) ?? [PrimaryDiagnosisCode.C61];

  const metastaticDiagnosisCodes =
    initialOrder.metastaticDiagnosisCodes?.filter(
      (code): code is MetastaticDiagnosisCode => !!code,
    ) ?? [];

  return {
    orderingProviderId: initialOrder.orderingProvider?.id ?? '',
    firstName: initialOrder.patient?.firstNamePhi ?? '',
    lastName: initialOrder.patient?.lastNamePhi ?? '',
    dateOfBirth: initialOrder.patient?.dateOfBirthPhi ?? '',
    ethnicity: initialOrder.patient?.ethnicity ?? '',
    patientStatus: (initialOrder.patientStatusAtBiopsy ??
      '') as PatientStatusAtBiopsy,
    dateOfDischarge: initialOrder.patientDischargeDate ?? '',
    primaryDiagnosisCodes,
    customPrimaryDiagnosisCode: initialOrder.customPrimaryDiagnosisCode ?? '',
    baselinePsa: initialOrder.psa ?? null,
    isOtherDiagnosisCodeSelected: !!initialOrder.customPrimaryDiagnosisCode,
    metastaticDiagnosisCodes,
    customMetastaticDiagnosisCode:
      initialOrder.customMetastaticDiagnosisCode ?? '',
    isOtherMetastaticDiagnosisCodeSelected:
      !!initialOrder.customMetastaticDiagnosisCode,
    tStage: (initialOrder.tStage ?? '') as TStage,
    dateOfProcedure: initialOrder.labProcedureDatePhi ?? '',
    patientLabAccession: initialOrder.patientLabAccession ?? '',
    slideSamplePart: initialOrder.slideSamplePart ?? '',
    testRequisitionForm,
    pathologyReport,
    faceSheet,
  };
};

export const LdtMetastaticProstateClinicOrderForm: FC<{
  orderRepository: OrderRepository;
  initialOrder: LdtOrderDetailFieldsFragment;
  onClose: () => void;
  workflowType: WorkflowType;
}> = ({ orderRepository, initialOrder, onClose, workflowType }) => {
  const isCSOrSalesUser = useIsLdtSalesOrCustomerSuccessUser();
  const isOrderingPhysicianUser = useIsOrderingPhysician();
  const allowTStageNotProvided = canUseClinicalNotProvided(
    isCSOrSalesUser,
    useEffectiveOrganization().features.allowTStageNotProvided,
    workflowType,
  );
  const theme = useTheme();
  const fetcher = useFetcher();
  const location = useLocation();
  const ldtOrderRepository = orderRepository as LdtOrderRepository;

  const orderingPathway = initialOrder.orderingPathway;

  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  const isEditingQCPendingOrder =
    isCSOrSalesUser &&
    initialOrder.status === OrderStatus.QualityControlPending;
  const softClinicalSubmit = allowSoftClinicalSubmit(
    isCSOrSalesUser,
    isEditingQCPendingOrder,
    workflowType,
  );

  const warningsRef = useRef<LdtMetastaticProstateClinicWarnings>({});
  const dateOfDischargeFieldRef = useRef<HTMLInputElement>(null);
  const customPrimaryDiagnosisCodeFieldRef = useRef<HTMLInputElement>(null);
  const customMetastaticDiagnosisCodeFieldRef = useRef<HTMLInputElement>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
    setValue,
    watch,
  } = useForm<LdtMetastaticProstateClinicFormData>({
    resolver: createMetastaticProstateClinicCustomResolver(
      isCSOrSalesUser,
      orderingPathway,
      isEditingQCPendingOrder,
      warningsRef,
      allowTStageNotProvided,
      workflowType,
    ),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues:
      createMetastaticProstateClinicDefaultFormValues(initialOrder),
  });

  useEffect(() => {
    if (isEditingQCPendingOrder) {
      trigger();
    }
  }, [isEditingQCPendingOrder, trigger]);

  const {
    selectedOrderingProvider,
    orderingProviderSectionProps,
    orderingProviderDialogProps,
  } = useOrderingProviderFormSection({
    ldtOrderRepository,
    isOrderingPhysicianUser,
    initialOrderingProvider: initialOrder.orderingProvider ?? null,
    setOrderingProviderId: (id, options) =>
      setValue('orderingProviderId', id, options),
    triggerOrderingProviderValidation: () => trigger('orderingProviderId'),
  });

  const patientStatus = watch('patientStatus');
  const isOtherDiagnosisCodeSelected = watch('isOtherDiagnosisCodeSelected');
  const isOtherMetastaticDiagnosisCodeSelected = watch(
    'isOtherMetastaticDiagnosisCodeSelected',
  );
  const tStage = watch('tStage');

  // Clear T3 / Not Provided when the current user is not allowed to keep them.
  useEffect(() => {
    if (!isCSOrSalesUser && tStage === TStage.T3) {
      setValue('tStage', '' as TStage, { shouldValidate: false });
    }
    if (!allowTStageNotProvided && tStage === TStage.NotProvided) {
      setValue('tStage', '' as TStage, { shouldValidate: false });
    }
  }, [isCSOrSalesUser, allowTStageNotProvided, tStage, setValue]);

  const previousPatientStatusRef = useRef<PatientStatusAtBiopsy | undefined>(
    patientStatus as PatientStatusAtBiopsy | undefined,
  );
  const prevIsOtherPrimaryDiagnosisCodeSelectedRef = useRef<boolean>(
    isOtherDiagnosisCodeSelected,
  );
  const prevIsOtherMetastaticDiagnosisCodeSelectedRef = useRef<boolean>(
    isOtherMetastaticDiagnosisCodeSelected,
  );

  // Clear discharge date when patient status switches away from hospital
  useEffect(() => {
    if (
      patientStatus === PatientStatusAtBiopsy.NonHospitalOutpatient ||
      patientStatus === PatientStatusAtBiopsy.NotProvided
    ) {
      setValue('dateOfDischarge', '', { shouldValidate: false });
    }
  }, [patientStatus, setValue]);

  // Auto-scroll to Date of Discharge when hospital status selected
  useEffect(() => {
    const isHospitalStatus =
      patientStatus === PatientStatusAtBiopsy.HospitalInpatient ||
      patientStatus === PatientStatusAtBiopsy.HospitalOutpatient;
    const wasHospitalStatus =
      previousPatientStatusRef.current ===
        PatientStatusAtBiopsy.HospitalInpatient ||
      previousPatientStatusRef.current ===
        PatientStatusAtBiopsy.HospitalOutpatient;

    if (
      !wasHospitalStatus &&
      isHospitalStatus &&
      dateOfDischargeFieldRef.current
    ) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          dateOfDischargeFieldRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          requestAnimationFrame(() => {
            dateOfDischargeFieldRef.current?.focus();
          });
        });
      });
    }

    previousPatientStatusRef.current = patientStatus as
      | PatientStatusAtBiopsy
      | undefined;
  }, [patientStatus]);

  // Auto-scroll to Custom ICD-10 field when "Other" is checked
  useEffect(() => {
    const wasOtherPrimaryDiagnosisCodeSelected =
      prevIsOtherPrimaryDiagnosisCodeSelectedRef.current;

    if (
      !wasOtherPrimaryDiagnosisCodeSelected &&
      isOtherDiagnosisCodeSelected &&
      customPrimaryDiagnosisCodeFieldRef.current
    ) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          customPrimaryDiagnosisCodeFieldRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          requestAnimationFrame(() => {
            customPrimaryDiagnosisCodeFieldRef.current?.focus();
          });
        });
      });
    }

    prevIsOtherPrimaryDiagnosisCodeSelectedRef.current =
      isOtherDiagnosisCodeSelected;
  }, [isOtherDiagnosisCodeSelected]);

  useEffect(() => {
    const wasOtherMetastaticDiagnosisCodeSelected =
      prevIsOtherMetastaticDiagnosisCodeSelectedRef.current;

    if (
      !wasOtherMetastaticDiagnosisCodeSelected &&
      isOtherMetastaticDiagnosisCodeSelected &&
      customMetastaticDiagnosisCodeFieldRef.current
    ) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          customMetastaticDiagnosisCodeFieldRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          requestAnimationFrame(() => {
            customMetastaticDiagnosisCodeFieldRef.current?.focus();
          });
        });
      });
    }

    prevIsOtherMetastaticDiagnosisCodeSelectedRef.current =
      isOtherMetastaticDiagnosisCodeSelected;
  }, [isOtherMetastaticDiagnosisCodeSelected]);

  const debouncedPsaValidation = useDebounce(() => {
    trigger('baselinePsa');
  }, 300);

  const {
    getFilesForCategory,
    handleFileSelected,
    handleFileRemoved,
    isFileDeleting,
  } = useLdtFileUploads({
    order: initialOrder,
    orderRepository: ldtOrderRepository,
    onFileCategoryUpdate: useCallback(
      (
        category: UploadedFileCategory,
        file?: LdtUploadedFileFieldsFragment,
        fileObject?: File,
      ) => {
        if (category === UploadedFileCategory.TestRequisitionForm) {
          setValue(
            'testRequisitionForm',
            file ? fileObject || new File([], file.filename) : null,
            { shouldValidate: true },
          );
        }
        if (category === UploadedFileCategory.PathologyReport) {
          setValue(
            'pathologyReport',
            file ? fileObject || new File([], file.filename) : null,
            { shouldValidate: true },
          );
        }
        if (category === UploadedFileCategory.FaceSheet) {
          setValue(
            'faceSheet',
            file ? fileObject || new File([], file.filename) : null,
            { shouldValidate: true },
          );
        }
      },
      [setValue],
    ),
    onError: notifyError,
  });

  const shouldShowDivider = useCallback(
    (index: number, totalItems: number) => {
      if (!isSmUp) return false;
      if (isLgUp) {
        const columnPosition = index % 4;
        return columnPosition !== 3 && index !== totalItems - 1;
      } else {
        const columnPosition = index % 2;
        return columnPosition !== 1 && index !== totalItems - 1;
      }
    },
    [isLgUp, isSmUp],
  );

  const trfFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.TestRequisitionForm),
    [getFilesForCategory],
  );
  const faceSheetFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.FaceSheet),
    [getFilesForCategory],
  );
  const pathologyReportFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.PathologyReport),
    [getFilesForCategory],
  );
  const insuranceFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.Insurance),
    [getFilesForCategory],
  );
  const consultReportFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.ConsultReport),
    [getFilesForCategory],
  );
  const attestationFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.Attestation),
    [getFilesForCategory],
  );
  const otherFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.Other),
    [getFilesForCategory],
  );

  const fileUploadConfigs = useMemo(() => {
    const isTrfRequired =
      isCSOrSalesUser &&
      orderingPathway !== OrderingPathway.StickerTrfNonReflexive;

    const configs: Array<{
      label: string;
      category: UploadedFileCategory;
      required?: boolean;
      helperText?: string;
      accept?: string;
      allowMultiple?: boolean;
      files: LdtUploadedFileFieldsFragment[];
      showDivider: (index: number, total: number) => boolean;
      visible: boolean;
    }> = [
      {
        label: 'Test Requisition Form',
        category: UploadedFileCategory.TestRequisitionForm,
        required: isTrfRequired,
        helperText: 'only one PDF file is allowed',
        accept: '.pdf',
        allowMultiple: false,
        files: trfFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: isTrfRequired,
      },
      {
        label: 'Demographic / Face Sheet',
        category: UploadedFileCategory.FaceSheet,
        required: isEditingQCPendingOrder,
        files: faceSheetFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: true,
      },
      {
        label: 'Pathology Report',
        category: UploadedFileCategory.PathologyReport,
        required: isEditingQCPendingOrder,
        files: pathologyReportFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: true,
      },
      {
        label: 'Insurance Card(s)',
        category: UploadedFileCategory.Insurance,
        files: insuranceFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: true,
      },
      {
        label: 'Attestation',
        category: UploadedFileCategory.Attestation,
        files: attestationFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: isCSOrSalesUser,
      },
      {
        label: 'Consult Report',
        category: UploadedFileCategory.ConsultReport,
        files: consultReportFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: isCSOrSalesUser,
      },
      {
        label: 'Other(s)',
        category: UploadedFileCategory.Other,
        files: otherFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: isCSOrSalesUser,
      },
    ];

    return configs.filter((config) => config.visible);
  }, [
    isCSOrSalesUser,
    isEditingQCPendingOrder,
    orderingPathway,
    trfFiles,
    faceSheetFiles,
    pathologyReportFiles,
    insuranceFiles,
    attestationFiles,
    consultReportFiles,
    otherFiles,
    shouldShowDivider,
  ]);

  const getOrderFromFormData = useCallback((): LdtOrderDetailFieldsFragment => {
    const formData = getValues();
    const { uploadedFiles: _uploadedFiles, ...orderWithoutFiles } =
      initialOrder;

    return {
      ...orderWithoutFiles,
      orderingProvider: {
        ...initialOrder.orderingProvider,
        id: formData.orderingProviderId || null,
      },
      patient: {
        ...initialOrder.patient,
        firstNamePhi: formData.firstName.trim() || null,
        lastNamePhi: formData.lastName.trim() || null,
        dateOfBirthPhi: formData.dateOfBirth || null,
        ethnicity: safeEmptyToUndefined(formData.ethnicity) as
          | Ethnicity
          | undefined,
      },
      psa: formData.baselinePsa,
      tStage: safeEmptyToUndefined(formData.tStage),
      patientStatusAtBiopsy: safeEmptyToUndefined(formData.patientStatus),
      primaryDiagnosisCodes: formData.primaryDiagnosisCodes,
      customPrimaryDiagnosisCode:
        formData.customPrimaryDiagnosisCode?.trim() || null,
      metastaticDiagnosisCodes: formData.metastaticDiagnosisCodes,
      customMetastaticDiagnosisCode:
        formData.customMetastaticDiagnosisCode?.trim() || null,
      patientDischargeDate: formData.dateOfDischarge || null,
      labProcedureDatePhi: formData.dateOfProcedure?.trim() || null,
      patientLabAccession: formData.patientLabAccession?.trim() || null,
      slideSamplePart: formData.slideSamplePart?.trim() || null,
      specimenType: SpecimenType.MetastaticProstateBiopsy,
      status: initialOrder.status,
    } as LdtOrderDetailFieldsFragment;
  }, [getValues, initialOrder]);

  useEffect(() => {
    const result = processFetcherResponse(fetcher.data, fetcher.state);

    if (result) {
      if (result.type === 'success') {
        notifySuccess(result.message);
        if (result.closeDialog) {
          onClose();
        }
      } else if (result.type === 'error') {
        notifyError(result.message);
      }
    }
  }, [fetcher.data, fetcher.state, onClose]);

  const isNewOrder = useNewOrderSignal(initialOrder.id);
  const isDraftOrder = initialOrder.status === OrderStatus.Draft;

  useEffect(() => {
    if (!isNewOrder) {
      trigger();
    }
  }, [isNewOrder, trigger]);

  const handleSaveOrder = async (
    qcRevisionReason?: string,
    revertStatus?: OrderStatus,
  ) => {
    const orderData = getOrderFromFormData();

    // When saving a QC Pending LDT order that has an AI report, set the revert status.
    if (revertStatus) {
      orderData.status = revertStatus;
      submitOrderAction(
        fetcher.submit,
        OrderAction.UPDATE_REJECTED_ORDER,
        location,
        orderData,
      );
      return;
    } else if (!orderData.id) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.CREATE,
        location,
        orderData,
      );
    } else {
      // Include qcRevisionReason if provided (convert empty string to null)
      if (qcRevisionReason) {
        orderData.qcRevisionReason = qcRevisionReason.trim() || null;
      }

      submitOrderAction(
        fetcher.submit,
        OrderAction.UPDATE,
        location,
        orderData,
      );
    }
  };

  const handleSubmitOrder = async () => {
    const orderData = getOrderFromFormData();
    const action =
      orderData.status === OrderStatus.Draft
        ? OrderAction.SUBMIT
        : OrderAction.UPDATE;
    submitOrderAction(fetcher.submit, action, location, orderData);
  };

  const handleDiscard = async () => {
    if (isDraftOrder && !isNewOrder) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.DELETE,
        location,
        undefined,
      );
    } else {
      notifySuccess('Order discarded successfully.');
      onClose();
    }
  };

  const hasAiReport = initialOrder.hasAiReport ?? false;

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(handleSubmitOrder)}
      noValidate
      sx={{
        width: '100%',
        height: '100%',
        maxWidth: '2000px',
        maxHeight: '1500px',
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'column',
        px: 2,
      }}
    >
      {/* Header */}
      <LdtOrderFormHeader
        title={initialOrder.crossOrgId ? 'Edit Order' : 'Create Order'}
        specimenType={SpecimenType.MetastaticProstateBiopsy}
        onClose={onClose}
        crossOrgId={initialOrder.crossOrgId}
      />
      <Typography
        variant='caption'
        sx={{
          color: 'text.secondary',
        }}
      >
        *Indicates a required field
      </Typography>
      {/* Scrollable content area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          mt: 2,
        }}
      >
        {/* Ordering Provider Section */}
        <OrderingProviderFormSection
          control={control}
          errors={errors}
          trigger={trigger}
          ldtOrderRepository={ldtOrderRepository}
          selectedOrderingProvider={selectedOrderingProvider}
          {...orderingProviderSectionProps}
        />
        <OrderingProviderForm
          orderRepository={ldtOrderRepository}
          {...orderingProviderDialogProps}
        />

        {/* Upload Document Section */}
        {fileUploadConfigs.length > 0 && (
          <FormSectionCard
            header={
              <SectionHeader
                title='Upload Document'
                subtitle='pdf, png, jpeg types are supported'
              />
            }
          >
            <Grid
              container
              spacing={2}
              sx={{
                mb: 1,
              }}
            >
              {fileUploadConfigs.map((config, index) => (
                <Grid key={config.category} size={{ xs: 12, sm: 6, lg: 3 }}>
                  <FileUpload
                    label={config.label}
                    category={config.category}
                    required={config.required}
                    helperText={config.helperText}
                    isFileDeleting={isFileDeleting}
                    validationText={
                      config.category ===
                      UploadedFileCategory.TestRequisitionForm
                        ? trfFiles.length > 0
                          ? undefined
                          : errors.testRequisitionForm?.message
                        : config.category ===
                            UploadedFileCategory.PathologyReport
                          ? pathologyReportFiles.length > 0
                            ? undefined
                            : errors.pathologyReport?.message
                          : config.category === UploadedFileCategory.FaceSheet
                            ? faceSheetFiles.length > 0
                              ? undefined
                              : errors.faceSheet?.message
                            : undefined
                    }
                    onFileSelected={handleFileSelected}
                    onFileRemoved={handleFileRemoved}
                    accept={config.accept}
                    allowMultiple={config.allowMultiple}
                    showDivider={config.showDivider(
                      index,
                      fileUploadConfigs.length,
                    )}
                    files={config.files}
                  />
                </Grid>
              ))}
            </Grid>
          </FormSectionCard>
        )}

        {/* Patient Information Section */}
        <PatientInformationFormSection control={control} errors={errors} />

        {/* Clinical Information Section */}
        <FormSectionCard
          header={<SectionHeader title='Clinical Information' />}
          sx={{ borderBottom: 'none' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <ResponsiveFormRow>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='baselinePsa'
                  control={control}
                  render={({ field }) => {
                    // Get warning from resolver (computed in resolver)
                    const warningText = warningsRef.current.baselinePsa;

                    return (
                      <DecimalInput
                        fullWidth
                        required={!softClinicalSubmit}
                        label='Most Recent Pre-Treatment PSA (ng/mL)'
                        value={field.value}
                        decimalPlaces={2}
                        min={FORM_CONSTANTS.PSA_MIN}
                        error={!!errors.baselinePsa}
                        validationText={errors.baselinePsa?.message}
                        warningText={warningText}
                        onBlur={field.onBlur}
                        onChange={(value) => {
                          field.onChange(value);
                          debouncedPsaValidation();
                        }}
                      />
                    );
                  }}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='tStage'
                  control={control}
                  render={({ field }) => (
                    <SelectList
                      {...field}
                      label='Clinical T-stage'
                      labelId='t-stage-label'
                      required
                      value={field.value ?? undefined}
                      onChange={(event) => {
                        field.onChange(event);
                        void trigger('tStage');
                      }}
                      onBlur={field.onBlur}
                      options={getProstateBiopsyTStageOptions(
                        isCSOrSalesUser,
                        allowTStageNotProvided,
                      )}
                      validationText={errors.tStage?.message?.toString() ?? ''}
                    />
                  )}
                />
              </Box>
            </ResponsiveFormRow>
          </Box>
        </FormSectionCard>

        {/* Specimen Information Section - CS/Sales only */}
        {isCSOrSalesUser && (
          <SpecimenInformationFormSection
            specimenType={SpecimenType.MetastaticProstateBiopsy}
            control={control}
            errors={errors}
            trigger={trigger}
            required={isEditingQCPendingOrder}
            warningsRef={warningsRef}
          />
        )}

        {/* Billing Information Section */}
        <BillingInformationFormSection
          control={control}
          errors={errors}
          trigger={trigger}
          specimenType={SpecimenType.MetastaticProstateBiopsy}
          patientStatus={patientStatus as string}
          dateOfDischargeFieldRef={dateOfDischargeFieldRef}
          customPrimaryDiagnosisCodeFieldRef={
            customPrimaryDiagnosisCodeFieldRef
          }
          workflowType={WorkflowType.CLINIC}
          clearCustomPrimaryDiagnosisCode={() => {
            setValue('customPrimaryDiagnosisCode', '', {
              shouldValidate: false,
            });
            setValue('isOtherDiagnosisCodeSelected', false, {
              shouldValidate: false,
            });
          }}
          customMetastaticDiagnosisCodeFieldRef={
            customMetastaticDiagnosisCodeFieldRef
          }
          clearCustomMetastaticDiagnosisCode={() => {
            setValue('customMetastaticDiagnosisCode', '', {
              shouldValidate: false,
            });
            setValue('isOtherMetastaticDiagnosisCodeSelected', false, {
              shouldValidate: false,
            });
          }}
        />
      </Box>
      {/* Fixed FormActions at the bottom */}
      <Box
        sx={{
          flexShrink: 0,
          mt: 2,
        }}
      >
        <LdtFormActions
          showSubmitButton={
            [WorkflowType.CLINIC, WorkflowType.DIRECT_UPLOAD_LAB].includes(
              workflowType,
            ) && isDraftOrder
          }
          showSaveButton={isEditingQCPendingOrder}
          showDraftActionButtons={
            (isDraftOrder || isNewOrder) &&
            [WorkflowType.CLINIC, WorkflowType.DIRECT_UPLOAD_LAB].includes(
              workflowType,
            )
          }
          isSubmitting={fetcher.state !== 'idle'}
          onValidateBeforeSubmit={async () => trigger()}
          onConfirmSubmit={handleSubmitOrder}
          onSaveAsDraft={handleSaveOrder}
          onDiscard={handleDiscard}
          requireDiscardConfirmation={!isNewOrder}
          onDiscardSuccess={() => {
            notifySuccess('Order discarded successfully.');
            onClose();
          }}
          showRevisionReasonDialog={!hasAiReport}
          showSaveWithReportDialog={hasAiReport}
          onConfirmSave={(qcRevisionReason) =>
            handleSaveOrder(qcRevisionReason)
          }
          onConfirmSaveWithReport={(revertStatus: OrderStatus) =>
            handleSaveOrder(undefined, revertStatus)
          }
          onFileSelected={handleFileSelected}
          onFileRemoved={handleFileRemoved}
          isFileDeleting={isFileDeleting}
          otherFiles={otherFiles}
          existingQcRevisionReason={initialOrder.qcRevisionReason}
        />
      </Box>

      <PreventLeaveDuringUploadDialog orderId={initialOrder.id} />
    </Box>
  );
};

// Helper function to compute PSA warning
const computePsaWarning = (psaValue?: number): string => {
  if (!psaValue || psaValue <= 100) return '';
  return 'PSA exceeds 100.00 ng/mL. Please verify that the PSA is correct before proceeding.';
};
